import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiPost } from '@/services/api/apiClient';
import { assessmentService } from '@/services/assessment/assessmentService';
import {
  Container,
  Paper,
  Typography,
  LinearProgress,
  Box,
  Button,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  useTheme,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Timer,
  Assignment,
  Warning,
  Send,
} from '@mui/icons-material';

interface Question {
  _id: string;
  question: string;
  options: {
    optionA: string;
    optionB: string;
    optionC: string;
    optionD: string;
  };
  category: string;
  difficulty: string;
}

interface AttemptData {
  _id: string;
  attemptNumber: number;
  timeLimit: number;
  startedAt: string;
}

interface AssessmentDetails {
  _id: string;
  title: string;
  description: string;
  difficulty: string;
  isActive: boolean;
  configuration: {
    timeLimit: number;
    questionCount: number;
    passCriteria: number;
  };
}

interface ExamData {
  attempt: AttemptData;
  questions: Question[];
}

export const ExamComponent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [assessmentDetails, setAssessmentDetails] = useState<AssessmentDetails | null>(null);
  const [examData, setExamData] = useState<ExamData | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initializedRef = useRef<string | null>(null);
  const [questionTimestamps, setQuestionTimestamps] = useState<Record<string, number>>({});
  const [examActive, setExamActive] = useState(false);
  const [showTimeUpDialog, setShowTimeUpDialog] = useState(false);
  const [autoSubmitCountdown, setAutoSubmitCountdown] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCongratulationsDialog, setShowCongratulationsDialog] = useState(false);

  // Log suspicious activities during exam
  const logSuspiciousActivity = async (activityType: string, details?: any) => {
    if (!examData?.attempt?._id) return;
    
    const logData = {
      attemptId: examData.attempt._id,
      activityType,
      timestamp: new Date().toISOString(),
      currentQuestion: currentQuestionIndex + 1,
      timeRemaining,
      details: details || {},
      userAgent: navigator.userAgent,
      url: window.location.href
    };
console.log(logData)
    try {
      completeAssessment();
      // Log to console for development
      // console.warn('🚨 SUSPICIOUS ACTIVITY DETECTED:', logData);
      
      // // Send to backend API (implement this endpoint on your backend)
      // await apiPost(`/assessment/attempts/${examData.attempt._id}/log-activity`, logData);
    } catch (error) {
      console.error('Failed to log suspicious activity:', error);
    }
  };

  useEffect(() => {
    if (id && initializedRef.current !== id) {
      initializedRef.current = id;
      loadAssessmentDetails(id);
      startAssessment(id);
    }
  }, [id]);

  // Track question start time when question changes
  useEffect(() => {
    if (examData && examData.questions[currentQuestionIndex]) {
      const currentQuestionId = examData.questions[currentQuestionIndex]._id;
      const now = Date.now();
      setQuestionTimestamps(prev => ({
        ...prev,
        [currentQuestionId]: now
      }));
    }
  }, [currentQuestionIndex, examData]);

  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setShowTimeUpDialog(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timeRemaining]);

  // Auto-submit countdown when time is up
  useEffect(() => {
    if (showTimeUpDialog && autoSubmitCountdown > 0 && !isSubmitting) {
      const countdownTimer = setTimeout(() => {
        setAutoSubmitCountdown(prev => {
          if (prev <= 1) {
            handleTimeUpSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearTimeout(countdownTimer);
    }
  }, [showTimeUpDialog, autoSubmitCountdown, isSubmitting]);

  // Page unload/refresh detection and prevention
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (examActive && examData) {
        logSuspiciousActivity('PAGE_UNLOAD_ATTEMPT', {
          reason: 'User attempted to close/refresh page during exam',
          currentQuestionIndex,
          answersSubmitted: Object.keys(answers).length
        });

        // Show browser confirmation dialog
        e.preventDefault();
        e.returnValue = 'You are in the middle of an exam. Are you sure you want to leave? Your progress may be lost.';
        return e.returnValue;
      }
    };

    const handleVisibilityChange = () => {
      if (examActive && document.hidden) {
        logSuspiciousActivity('TAB_SWITCH', {
          reason: 'User switched away from exam tab',
          visibilityState: document.visibilityState,
          currentQuestionIndex
        });
      }
    };

    const handleFocus = () => {
      if (examActive) {
        logSuspiciousActivity('WINDOW_FOCUS', {
          reason: 'User returned focus to exam window',
          currentQuestionIndex
        });
      }
    };

    const handleBlur = () => {
      if (examActive) {
        logSuspiciousActivity('WINDOW_BLUR', {
          reason: 'User lost focus from exam window',
          currentQuestionIndex
        });
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [examActive, examData, currentQuestionIndex, answers]);

  const calculateTimeSpent = (questionId: string): number => {
    const startTime = questionTimestamps[questionId];
    if (!startTime) return 0;
    
    const timeSpentMs = Date.now() - startTime;
    return Math.round(timeSpentMs / 1000); // Convert to seconds
  };

  const loadAssessmentDetails = async (assessmentId: string) => {
    try {
      const response = await assessmentService.getAssessment(assessmentId);
      if (response.success && response.data) {
        setAssessmentDetails(response.data.assessment);
      }
    } catch (err) {
      console.error('Failed to load assessment details:', err);
    }
  };

  const startAssessment = async (assessmentId: string) => {
    try {
      setLoading(true);
      
      const data = await apiPost(`/assessment/assessments/${assessmentId}/start`);
      
      if (data.success) {
        setExamData(data.data);
        setTimeRemaining(data.data.attempt.timeLimit * 60);
        setExamActive(true);
        
        // // Log exam start
        // logSuspiciousActivity('EXAM_STARTED', {
        //   reason: 'User successfully started the exam',
        //   attemptId: data.data.attempt._id,
        //   timeLimit: data.data.attempt.timeLimit
        // });
      } else {
        throw new Error(data.message || 'Failed to start assessment');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start assessment');
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async (questionId: string, selectedAnswer: string, timeSpent: number) => {
    try {
      await apiPost(`/assessment/attempts/${examData?.attempt._id}/answer`, {
        questionId,
        selectedAnswer,
        timeSpent,
      });
    } catch (err) {
      console.error('Failed to submit answer:', err);
    }
  };

  const completeAssessment = async () => {
    if(examActive && !isSubmitting){
    try {
      setExamActive(false); // Disable page unload prevention
      setIsSubmitting(true);
      
      await apiPost(`/assessment/attempts/${examData?.attempt._id}/complete`);
      setShowCongratulationsDialog(true);
    } catch (err) {
      // console.error('Failed to complete assessment:', err);
      setExamActive(true); // Re-enable if completion failed
      setIsSubmitting(false);
    }
  }
  };

  const handleCongratulationsClose = () => {
    setShowCongratulationsDialog(false);
    navigate('/attempts');
  };

  const handleTimeUpSubmit = async () => {
    if (!examData) return;
    
    setIsSubmitting(true);
    
    // Submit current answer if any
    const currentQuestion = examData.questions[currentQuestionIndex];
    const selectedAnswer = answers[currentQuestion._id];
    const timeSpent = calculateTimeSpent(currentQuestion._id);
    
    if (selectedAnswer) {
      await submitAnswer(currentQuestion._id, selectedAnswer, timeSpent);
    }
    
    // Complete the assessment
    await completeAssessment();
  };

  const handleManualSubmit = async () => {
    setIsSubmitting(true);
    await handleTimeUpSubmit();
  };

  const handleAnswerChange = (value: string) => {
    if (!examData) return;
    
    const currentQuestion = examData.questions[currentQuestionIndex];
    setAnswers(prev => ({
      ...prev,
      [currentQuestion._id]: value,
    }));
  };

  const handleNext = async () => {
    if (!examData) return;
    
    const currentQuestion = examData.questions[currentQuestionIndex];
    const selectedAnswer = answers[currentQuestion._id];
    const timeSpent = calculateTimeSpent(currentQuestion._id);
    
    if (selectedAnswer) {
      await submitAnswer(currentQuestion._id, selectedAnswer, timeSpent);
    }

    if (currentQuestionIndex === examData.questions.length - 1) {
      await completeAssessment();
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography>Loading assessment...</Typography>
        </Box>
      </Container>
    );
  }

  if (error || !examData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Failed to load assessment'}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/assessment')}>
          Back to Assessments
        </Button>
      </Container>
    );
  }

  const currentQuestion = examData.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex ) / examData.questions.length) * 100;

  return (
    <Container maxWidth="lg" sx={{ py: 4, minHeight: '100vh' }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper',
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {assessmentDetails?.title || 'Assessment Exam'}
              </Typography>
              {assessmentDetails && (
                <Typography variant="body1" color="text.secondary">
                  {assessmentDetails.description}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Timer color="primary" />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: timeRemaining < 300 ? 'error.main' : 'primary.main',
                }}
              >
                {formatTime(timeRemaining)}
              </Typography>
            </Box>
          </Box>

          {/* Assessment Details */}
          {assessmentDetails && (
            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Difficulty:</strong> {assessmentDetails.difficulty}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Pass Criteria:</strong> {assessmentDetails.configuration.passCriteria}%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total Questions:</strong> {assessmentDetails.configuration.questionCount}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Question {currentQuestionIndex + 1} of {examData.questions.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress)}% Complete
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
              }}
            />
          </Box>
        </Box>

        {/* Question Card */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Assignment sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Question {currentQuestionIndex + 1}
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mb: 4, lineHeight: 1.6 }}>
              {currentQuestion.question}
            </Typography>

            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <RadioGroup
                value={answers[currentQuestion._id] || ''}
                onChange={(e) => handleAnswerChange(e.target.value)}
              >
                {Object.entries(currentQuestion.options).map(([key, value]) => (
                  <FormControlLabel
                    key={key}
                    value={key.slice(-1)}
                    control={<Radio />}
                    label={
                      <Typography variant="body1" sx={{ py: 1 }}>
                        {value}
                      </Typography>
                    }
                    sx={{
                      mb: 1,
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                        bgcolor: theme.palette.action.hover,
                      },
                      '&.Mui-checked': {
                        bgcolor: theme.palette.primary.main + '10',
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Previous
          </Button>

          <Typography variant="body2" color="text.secondary">
            {currentQuestionIndex + 1} / {examData.questions.length}
          </Typography>

          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={handleNext}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {currentQuestionIndex === examData.questions.length - 1 ? 'Submit' : 'Next'}
          </Button>
        </Box>
      </Paper>

      {/* Time Up Dialog */}
      <Dialog
        open={showTimeUpDialog}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              p: 1
            }
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ fontSize: 48, color: 'warning.main' }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
              Time's Up!
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Your assessment time has expired
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You can submit your assessment now or it will be automatically submitted in:
          </Typography>
          
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 2,
              p: 3,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'warning.main'
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
              {autoSubmitCountdown}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              seconds remaining
            </Typography>
            
            {isSubmitting && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Submitting assessment...</Typography>
              </Box>
            )}
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            Your current answers will be saved automatically
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', p: 3, pt: 0 }}>
          <Button
            onClick={handleManualSubmit}
            variant="contained"
            size="large"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: '1.1rem'
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Congratulations Dialog */}
      <Dialog
        open={showCongratulationsDialog}
        onClose={handleCongratulationsClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: 8
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: 3
              }}
            >
              <Assignment sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
              Congratulations!
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            You have successfully completed your test
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your answers have been submitted and your results will be processed shortly.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', p: 3, pt: 0 }}>
          <Button
            onClick={handleCongratulationsClose}
            variant="contained"
            size="large"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: '1.1rem',
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              }
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};